import { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useAuth } from '../contexts/AuthContext';
import Layout from '../components/Layout';
import { SearchableSelect } from '../components/SearchableSelect';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import api from '../lib/api';
import { Send, LogOut, CheckCircle, Plus, Search } from 'lucide-react';
import { toast } from 'sonner';
import { ImportDialog } from '../components/ImportDialog';
import { apiErrorMessage } from '../utils/requestTheme';


export default function InternalPaymentForm() {
  const { user, logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [requestTypes, setRequestTypes] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [lastRequestNumber, setLastRequestNumber] = useState('');

  const [formData, setFormData] = useState({
    tipe_request: '',
    company_id: '',
    departemen_id: '',
    amount: '',
    penerima_user_id: '',
    keterangan: ''
  });

  const [tipeSearch, setTipeSearch] = useState('');
  const [showTipeDropdown, setShowTipeDropdown] = useState(false);
  const [selectedPenerima, setSelectedPenerima] = useState(null);

  const isInternalOnly = user?.role === 'internal';

  const selectedPenerimaIdRef = useRef('');
  useEffect(() => {
    selectedPenerimaIdRef.current = formData.penerima_user_id;
  }, [formData.penerima_user_id]);

  // Terpisah dari fetchInitialData supaya daftar penerima bisa dimuat ulang
  // sendiri: approval bisa menambah atau menghapus penerima kapan saja, dan
  // form ini sering dibiarkan terbuka lama.
  const fetchRecipients = useCallback(async () => {
    try {
      const r = await api.get(`/internal/approval-users`);
      setRecipients(r.data);
      // Pilihan yang sudah dihapus harus dilepas, bukan dibiarkan terkirim:
      // backend kini menolaknya dengan 400. Dibaca lewat ref supaya callback
      // ini tidak ikut berubah setiap kali form diisi.
      const selectedId = selectedPenerimaIdRef.current;
      if (selectedId && !r.data.some(x => x.id === selectedId)) {
        setFormData(prev => ({ ...prev, penerima_user_id: '' }));
        setSelectedPenerima(null);
        toast.warning('Penerima yang Anda pilih sudah dihapus. Silakan pilih penerima lain.');
      }
    } catch (e) { console.error('Failed to fetch recipients:', e); }
  }, []);

  const fetchInitialData = useCallback(async () => {
    setLoading(true);
    // Fetch each independently - if one fails, others still load
    try { const r = await api.get(`/internal/request-types`); setRequestTypes(r.data); } catch (e) { console.error('Failed to fetch request types:', e); }
    // Hanya perusahaan kategori IPR yang muncul di form Internal.
    try { const r = await api.get(`/companies`, { params: { category: 'ipr' } }); setCompanies(r.data); } catch (e) { console.error('Failed to fetch companies:', e); }
    try { const r = await api.get(`/departments`); setDepartments(r.data); } catch (e) { console.error('Failed to fetch departments:', e); }
    await fetchRecipients();
    setLoading(false);
  }, [fetchRecipients]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  // Muat ulang saat tab kembali aktif, supaya dropdown tidak basi.
  useEffect(() => {
    const revalidate = () => {
      if (document.visibilityState === 'visible') fetchRecipients();
    };
    window.addEventListener('focus', revalidate);
    document.addEventListener('visibilitychange', revalidate);
    return () => {
      window.removeEventListener('focus', revalidate);
      document.removeEventListener('visibilitychange', revalidate);
    };
  }, [fetchRecipients]);

  const filteredTypes = useMemo(() => {
    if (!tipeSearch) return requestTypes;
    return requestTypes.filter(t => t.toLowerCase().includes(tipeSearch.toLowerCase()));
  }, [tipeSearch, requestTypes]);

  const handleTipeSelect = (type) => {
    setFormData(prev => ({ ...prev, tipe_request: type }));
    setTipeSearch(type);
    setShowTipeDropdown(false);
  };

  const handlePenerimaSelect = (e) => {
    const id = e.target.value;
    const penerima = recipients.find(u => u.id === id);
    setFormData(prev => ({ ...prev, penerima_user_id: id }));
    setSelectedPenerima(penerima || null);
  };

  const formatCurrency = (value) => {
    const numericValue = value.replace(/\D/g, '');
    return numericValue.replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  };

  const handleAmountChange = (e) => {
    setFormData(prev => ({ ...prev, amount: formatCurrency(e.target.value) }));
  };

  const parseAmount = (formatted) => parseFloat(formatted.replace(/\./g, '')) || 0;

  const resetForm = () => {
    setFormData({ tipe_request: '', company_id: '', departemen_id: '', amount: '', penerima_user_id: '', keterangan: '' });
    setTipeSearch('');
    setSelectedPenerima(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.tipe_request) return toast.error('Tipe Request wajib dipilih');
    if (!formData.company_id) return toast.error('Perusahaan wajib dipilih');
    if (!formData.departemen_id) return toast.error('Departemen wajib dipilih');
    if (!formData.amount || parseAmount(formData.amount) <= 0) return toast.error('Amount wajib diisi');
    if (!formData.penerima_user_id) return toast.error('Nama Penerima wajib dipilih');

    setSubmitting(true);
    try {
      const res = await api.post(`/internal/payment-requests`, {
        tipe_request: formData.tipe_request,
        company_id: formData.company_id,
        departemen_id: formData.departemen_id,
        amount: parseAmount(formData.amount),
        penerima_user_id: formData.penerima_user_id,
        keterangan: formData.keterangan || ""
      });
      setLastRequestNumber(res.data.request_number);
      setShowSuccessDialog(true);
      toast.success('Payment request berhasil diajukan!');
    } catch (error) {
      toast.error(apiErrorMessage(error, 'Gagal mengajukan payment request'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/login';
  };

  if (loading) {
    return isInternalOnly ? (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
      </div>
    ) : (
      <Layout>
        <div className="flex items-center justify-center h-96">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
      </Layout>
    );
  }

  const formContent = (
    <>
      <Card className="max-w-2xl mx-auto shadow-xl border-red-200" data-testid="internal-payment-form">
        <CardHeader className="bg-gradient-to-r from-red-600 to-red-700 text-white rounded-t-lg">
          <CardTitle className="flex items-center gap-2">
            <Send className="h-5 w-5" />
            Form Pengajuan Payment Request
          </CardTitle>
          <div className="flex flex-wrap items-center justify-between gap-2">
            <p className="text-red-100 text-sm">Isi form di bawah untuk mengajukan pembayaran</p>
            {['internal', 'admin'].includes(user?.role) && (
              <ImportDialog type="ipr" title="Internal Payment Request (IPR)" label="Import dari Excel"
                className="bg-white/10 border-white/40 text-white hover:bg-white/20 hover:text-white" testId="import-ipr" />
            )}
          </div>
        </CardHeader>
        <CardContent className="p-6">
          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Tipe Request */}
            <div className="space-y-2">
              <Label className="font-semibold">Tipe Request <span className="text-red-500">*</span></Label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  data-testid="tipe-request-input"
                  placeholder="Cari atau pilih tipe request..."
                  value={tipeSearch}
                  onChange={(e) => { setTipeSearch(e.target.value); setFormData(prev => ({ ...prev, tipe_request: '' })); setShowTipeDropdown(true); }}
                  onFocus={() => setShowTipeDropdown(true)}
                  className="pl-10"
                />
                {showTipeDropdown && (
                  <div className="absolute z-10 w-full mt-1 bg-white border rounded-lg shadow-lg max-h-60 overflow-y-auto" data-testid="tipe-request-dropdown">
                    {filteredTypes.length > 0 ? filteredTypes.map((type, i) => (
                      <button key={type} type="button" className="w-full text-left px-4 py-2.5 hover:bg-red-50 text-sm border-b border-slate-100 last:border-0" onClick={() => handleTipeSelect(type)} data-testid={`tipe-option-${i}`}>
                        {type}
                      </button>
                    )) : <p className="px-4 py-3 text-sm text-slate-500">Tidak ada hasil</p>}
                  </div>
                )}
              </div>
              {formData.tipe_request && <p className="text-xs text-emerald-600 flex items-center gap-1"><CheckCircle className="h-3 w-3" /> {formData.tipe_request}</p>}
            </div>

            {/* Company */}
            <div className="space-y-2">
              <Label className="font-semibold">Perusahaan <span className="text-red-500">*</span></Label>
              <SearchableSelect
                options={companies}
                value={formData.company_id}
                onChange={(val) => setFormData(prev => ({ ...prev, company_id: val }))}
                displayKey="nama_perusahaan"
                valueKey="id"
                placeholder="Ketik nama perusahaan..."
                testId="company-select"
              />
            </div>

            {/* Departemen */}
            <div className="space-y-2">
              <Label className="font-semibold">Departemen <span className="text-red-500">*</span></Label>
              <SearchableSelect
                options={departments}
                value={formData.departemen_id}
                onChange={(val) => setFormData(prev => ({ ...prev, departemen_id: val }))}
                displayKey="nama"
                valueKey="id"
                placeholder="Ketik nama departemen..."
                testId="departemen-select"
              />
              {!departments.length && <p className="text-xs text-red-600">Belum ada departemen. Hubungi Admin.</p>}
            </div>

            {/* Amount */}
            <div className="space-y-2">
              <Label className="font-semibold">Amount (Rp) <span className="text-red-500">*</span></Label>
              <div className="relative">
                <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500 font-semibold">Rp</span>
                <Input data-testid="amount-input" placeholder="0" value={formData.amount} onChange={handleAmountChange} className="pl-10 font-mono text-right" />
              </div>
            </div>

            {/* Penerima */}
            <div className="space-y-4 p-4 bg-slate-50 rounded-lg border">
              <h3 className="font-semibold text-slate-800 border-b pb-2">Data Penerima</h3>
              <div className="space-y-2">
                <Label className="font-semibold">Nama Penerima <span className="text-red-500">*</span></Label>
                <SearchableSelect
                  options={recipients.map(r => ({ ...r, display_name: `${r.name}${r.nomor_rekening ? ` (${r.nama_bank || ''} - ${r.nomor_rekening})` : ''}` }))}
                  value={formData.penerima_user_id}
                  onChange={(val) => {
                    const penerima = recipients.find(u => u.id === val);
                    setFormData(prev => ({ ...prev, penerima_user_id: val }));
                    setSelectedPenerima(penerima || null);
                  }}
                  displayKey="display_name"
                  valueKey="id"
                  placeholder="Ketik nama penerima..."
                  testId="penerima-select"
                />
              </div>
              {selectedPenerima && (
                <div className="grid grid-cols-2 gap-4 pt-2">
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">No. Rekening</Label>
                    <Input data-testid="penerima-rekening" value={selectedPenerima.nomor_rekening || '-'} disabled className="bg-white font-mono" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Nama Bank</Label>
                    <Input data-testid="penerima-bank" value={selectedPenerima.nama_bank || '-'} disabled className="bg-white" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">No. WA</Label>
                    <Input data-testid="penerima-wa" value={selectedPenerima.nomor_wa || '-'} disabled className="bg-white" />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-xs text-slate-500">Email</Label>
                    <Input data-testid="penerima-email" value={selectedPenerima.email || '-'} disabled className="bg-white" />
                  </div>
                </div>
              )}
            </div>

            {/* Keterangan */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Keterangan</Label>
              <textarea
                data-testid="keterangan-input"
                className="flex min-h-[80px] w-full rounded-md border border-input bg-white px-3 py-2 text-sm uppercase focus:ring-2 focus:ring-red-500"
                value={formData.keterangan}
                onChange={(e) => setFormData(prev => ({ ...prev, keterangan: e.target.value.toUpperCase() }))}
                placeholder="MASUKKAN KETERANGAN (OPSIONAL)..."
              />
            </div>

            {/* Buttons */}
            <div className="flex gap-3">
              <Button type="submit" disabled={submitting} className="flex-1 bg-red-600 hover:bg-red-700 text-white py-6 text-lg font-semibold" data-testid="submit-payment-button">
                {submitting ? 'Mengajukan...' : <><Send className="mr-2 h-5 w-5" /> Ajukan Payment</>}
              </Button>
              {isInternalOnly && (
                <Button type="button" variant="outline" onClick={handleLogout} className="border-slate-300 py-6" data-testid="form-logout-button">
                  <LogOut className="h-5 w-5" />
                </Button>
              )}
            </div>
          </form>
        </CardContent>
      </Card>

      {/* Success Dialog */}
      <Dialog open={showSuccessDialog} onOpenChange={setShowSuccessDialog}>
        <DialogContent className="max-w-md" data-testid="success-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-emerald-600">
              <CheckCircle className="h-6 w-6" /> Payment Request Berhasil!
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-emerald-50 p-4 rounded-lg border border-emerald-200 text-center">
              <p className="text-sm text-emerald-600 mb-1">Nomor Request</p>
              <p className="font-mono font-bold text-xl text-emerald-700">{lastRequestNumber}</p>
            </div>
            <p className="text-center text-slate-600">Data dikirim ke Approval dengan status <strong>"Proses Bayar"</strong>.</p>
            <div className="grid grid-cols-2 gap-3">
              <Button onClick={() => { setShowSuccessDialog(false); resetForm(); }} className="bg-red-600 hover:bg-red-700" data-testid="add-another-button">
                <Plus className="mr-2 h-4 w-4" /> Tambah Lagi
              </Button>
              {isInternalOnly ? (
                <Button onClick={handleLogout} variant="outline" data-testid="logout-button">
                  <LogOut className="mr-2 h-4 w-4" /> Logout
                </Button>
              ) : (
                <Button onClick={() => setShowSuccessDialog(false)} variant="outline" data-testid="close-dialog-button">
                  Tutup
                </Button>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {showTipeDropdown && <div className="fixed inset-0 z-0" onClick={() => setShowTipeDropdown(false)} />}
    </>
  );

  // Internal users get standalone layout, Approval/Admin get sidebar layout
  if (isInternalOnly) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-red-50 to-red-100 p-4 md:p-8">
        <div className="max-w-2xl mx-auto mb-6">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-bold text-red-700" style={{ fontFamily: 'Manrope, sans-serif' }}>HISA</h1>
              <p className="text-red-500 text-sm">Internal Payment Request</p>
            </div>
            <div className="flex items-center gap-3">
              <div className="text-right">
                <p className="text-sm font-semibold">{user?.name}</p>
                <p className="text-xs text-red-600">Internal User</p>
              </div>
              <Button variant="outline" size="sm" onClick={handleLogout} className="border-red-300 text-red-600 hover:bg-red-50" data-testid="header-logout-button">
                <LogOut className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
        {formContent}
      </div>
    );
  }

  return (
    <Layout>
      <div className="space-y-6">
        <h1 className="text-3xl font-bold" style={{ fontFamily: 'Manrope, sans-serif' }}>Buat Internal Payment Request</h1>
        {formContent}
      </div>
    </Layout>
  );
}



